import React, { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";


export default function QuestionUploadPage() {
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { authToken } = useSelector((state) => state.login);

  const base = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_TYPE}`;


  const getQuestionSet=async()=>{
    try {
      const response = await axios.get(`${base}/exam/questionSets`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      setPreview(response?.data?.data?.questionSets[0]?.questions)

      
    } catch (err) {
      console.error("❌ Failed to download template:", err);
    }
  }

  useEffect(()=>{
   getQuestionSet()
  },[])

  // 🧾 Download CSV Template from API
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${base}/exam/downloadTemplate`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "question_template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("❌ Failed to download template:", err);
      alert("Failed to download template. Please try again later.");
    }
  };

  

  // 📤 Handle CSV Upload & Validation


const handleFileUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  // allow re-uploading the same file again
  e.target.value = null;
  setError("");
  setSuccess("");
  setPreview([]);

  // ✅ Only allow CSV files
  if (!file.name.toLowerCase().endsWith(".csv")) {
    setError("❌ Only CSV files are allowed. Please upload a .csv file.");
    return;
  }

  try {
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(`${base}/exam/uploadQuestions`, formData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "multipart/form-data",
      },
    });

    if (res.status === 200 || res.status === 201) {
      setSuccess("✅ Questions uploaded successfully!");
      getQuestionSet(); // refresh question set list
    } else {
      setError("❌ Upload failed. Please try again.");
    }
  } catch (err) {
    console.error("Upload error:", err);
    setError(
      err.response?.data?.message ||
        "❌ Server error while uploading questions."
    );
  } finally {
    setUploading(false);
  }
};








  // 📊 Table Columns
  const columns = useMemo(
    () => [
      { accessorKey: "order", header: "No." },
      { accessorKey: "question", header: "Question" },
      { accessorKey: "optionA", header: "Option 1" },
      { accessorKey: "optionB", header: "Option 2" },
      { accessorKey: "optionC", header: "Option 3" },
      { accessorKey: "optionD", header: "Option 4" },
      {
        accessorKey: "correct_answer",
        header: "Correct Answer",
        cell: (info) => (
          <span className="font-semibold text-green-600">
            {info.getValue()}
          </span>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: preview,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="max-w-7xl mx-auto p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">
        Upload Question Set
      </h1>

      {/* Actions */}
      <div className="flex flex-wrap justify-end gap-4 mb-8">
        <button
          onClick={()=>handleDownloadTemplate()}
          className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          📄 Download CSV Template
        </button>

        <label className="px-5 py-2 bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer transition">
          📤 Upload CSV
          <input
            type="file"
            accept=".csv," //.xlsx,.xls
            className="hidden"
            onChange={handleFileUpload}
            disabled={uploading}
          />
        </label>
      </div>

      {/* Alerts */}
      {uploading && (
        <div className="mb-4 text-center text-blue-600 animate-pulse">
          Uploading questions to server...
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Table Preview */}
      {preview?.length > 0 && (
        <div className="bg-white rounded shadow-md overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700 border-collapse">
            <thead className="bg-gray-200">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className="py-3 px-4 text-left font-semibold cursor-pointer select-none align-top"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b hover:bg-gray-50 transition align-top"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="py-3 px-4 align-top whitespace-pre-wrap break-words"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!preview?.length && !uploading && !error && (
        <div className="text-gray-500 text-center mt-20">
          Upload a CSV file to validate and upload your questions.
        </div>
      )}
    </div>
  );
}
